import React from 'react';
import { motion } from 'framer-motion';
import { 
  FileText, Server, Users, Mail, Brain, Zap,
  CheckCircle, ArrowRight, Globe, Database
} from 'lucide-react';

const FeatureShowcase = () => {
  const steps = [
    {
      icon: <FileText className="w-6 h-6" />,
      title: "Describe Your Product",
      description: "One sentence about what you sell. Takes 30 seconds.",
      details: "Example: 'AI copilot for real estate agents'. That's it.",
      color: "blue"
    },
    {
      icon: <Server className="w-6 h-6" />,
      title: "You Get john@techgrowth.io",
      description: "Pick from 70 domains with 10,000+ sent emails",
      details: "Inbox rate: 94.7%. SPF, DKIM, DMARC all green. Ready now.",
      color: "purple"
    },
    {
      icon: <Database className="w-6 h-6" />,
      title: "Find 1,847 VP Sales in 47 Seconds",
      description: "\"Show me VPs at Series-A SaaS companies\"",
      details: "AI finds decision makers. With verified emails. No bounces.",
      color: "green"
    },
    {
      icon: <Brain className="w-6 h-6" />,
      title: "Personalized at Scale",
      description: "AI writes unique emails for each prospect",
      details: "Combines your product context with each contact's details for authentic personalization.",
      color: "orange"
    },
    {
      icon: <Mail className="w-6 h-6" />,
      title: "Autopilot Sending",
      description: "30,000 emails/day start sending automatically",
      details: "Zero manual work. Replies come to your inbox. AI books meetings automatically.",
      color: "pink"
    }
  ];

  const getColorClasses = (color: string) => {
    const colors = {
      blue: "bg-blue-100 text-blue-600 border-blue-200",
      purple: "bg-purple-100 text-purple-600 border-purple-200",
      green: "bg-green-100 text-green-600 border-green-200",
      orange: "bg-orange-100 text-orange-600 border-orange-200",
      pink: "bg-pink-100 text-pink-600 border-pink-200"
    };
    return colors[color as keyof typeof colors] || colors.blue;
  };

  return (
    <section className="py-20 bg-gradient-to-b from-gray-50 to-white">
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center mb-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mb-6"
          >
            <div className="inline-flex items-center gap-2 bg-green-100 text-green-800 px-4 py-2 rounded-full text-sm font-medium mb-4">
              <Zap className="w-4 h-4" />
              How It Actually Works
            </div>
          </motion.div>
          
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
            className="text-4xl md:text-5xl font-bold text-gray-900 mb-6"
          >
            From Product Brief to Booked Meetings
          </motion.h2>
          
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
            className="text-xl text-gray-600 max-w-3xl mx-auto"
          >
            No DNS setup. No list building. No email writing. Just paste your product info and watch meetings appear.
          </motion.p>
        </div>

        {/* Process Steps */}
        <div className="relative">
          {/* Connection Line */}
          <div className="absolute top-1/2 left-0 right-0 h-0.5 bg-gray-200 hidden lg:block" />
          
          <div className="grid lg:grid-cols-5 gap-8 relative">
            {steps.map((step, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="relative"
              >
                <div className="bg-white rounded-xl p-6 shadow-lg hover:shadow-xl transition-all duration-300 h-full">
                  {/* Step Number */}
                  <div className="absolute -top-3 -right-3 w-8 h-8 bg-gray-900 text-white rounded-full flex items-center justify-center text-sm font-bold">
                    {index + 1}
                  </div>
                  
                  {/* Icon */}
                  <div className={`w-16 h-16 rounded-lg flex items-center justify-center mb-4 border ${getColorClasses(step.color)}`}>
                    {step.icon}
                  </div>
                  
                  {/* Content */}
                  <h3 className="font-semibold text-gray-900 mb-2">
                    {step.title}
                  </h3>
                  <p className="text-sm text-gray-600 mb-3">
                    {step.description}
                  </p>
                  <p className="text-xs text-gray-500">
                    {step.details}
                  </p>
                </div>
                
                {/* Arrow (except for last item) */}
                {index < steps.length - 1 && (
                  <div className="hidden lg:flex absolute top-1/2 -right-4 transform -translate-y-1/2 z-10">
                    <div className="w-8 h-8 bg-white rounded-full flex items-center justify-center shadow-md">
                      <ArrowRight className="w-4 h-4 text-gray-600" />
                    </div>
                  </div>
                )}
              </motion.div>
            ))}
          </div>
        </div>

        {/* Key Differentiators */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mt-20 grid md:grid-cols-3 gap-8"
        >
          <div className="bg-blue-50 rounded-xl p-6 border border-blue-200">
            <Globe className="w-10 h-10 text-blue-600 mb-4" />
            <h4 className="font-semibold text-gray-900 mb-2">
              70+ Pre-Warmed Domains
            </h4>
            <p className="text-sm text-gray-600">
              Skip the 2-month warmup period. Start sending from high-reputation infrastructure immediately.
            </p>
          </div>
          
          <div className="bg-green-50 rounded-xl p-6 border border-green-200">
            <Database className="w-10 h-10 text-green-600 mb-4" />
            <h4 className="font-semibold text-gray-900 mb-2">
              250M+ Shared Contact Pool
            </h4>
            <p className="text-sm text-gray-600">
              No need to buy lists. AI searches our entire database to find your perfect prospects.
            </p>
          </div>
          
          <div className="bg-purple-50 rounded-xl p-6 border border-purple-200">
            <Brain className="w-10 h-10 text-purple-600 mb-4" />
            <h4 className="font-semibold text-gray-900 mb-2">
              True AI Personalization
            </h4>
            <p className="text-sm text-gray-600">
              Not mail merge. Each email is uniquely written based on the prospect's profile.
            </p>
          </div>
        </motion.div>

        {/* Bottom CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mt-16"
        >
          <p className="text-lg text-gray-700 mb-6">
            <span className="font-semibold">Setup time: 2 minutes.</span> First replies: within 24 hours.
          </p>
        </motion.div>
      </div>
    </section>
  );
};

export default FeatureShowcase; 